import type { QueryClient } from '@tanstack/react-query'
import { z } from 'zod'

import { ChatSendValidationError } from '@/components/chat-v2/lib/ChatSendValidationError'
import type { PursuitDocumentMessageParams } from '@/components/chat-v2/lib/pursuitDocumentCreateMessage'
import { mergePersistedAndLiveSubagentSnapshots } from '@/components/chat-v2/lib/subagentEvents'
import {
  buildAllMessagesFromV3Sync,
  buildCurrentThreadFromV3Sync,
  buildOverlayMessagesFromV3Sync,
  buildPersistedRunMessagesFromV3Sync,
  collectKnownV3SyncObjectIds,
  createEmptyV3SyncCache,
  decideWhetherToSyncV3Digest,
  getCompactionStatusFromV3Overlay,
  mergeV3SyncCache,
  resolveCurrentModelNameFromV3Sync,
  toActiveRunIdFromV3Digest,
  toChatRunStateFromV3StreamState,
  serializeV3SyncFetch,
} from '@/components/chat-v2/lib/v3Sync'

import { API_URL } from '@/constants/env'

import { SweetspotError } from '@/helpers/error'

import {
  ChatCompactionStatus,
  ChatMessage,
  ChatRunState,
  PursuitDocumentChatSession,
  PursuitDocumentChatSyncCache,
  PursuitDocumentChatSyncDigest,
  PursuitDocumentChatSyncManifest,
  PursuitDocumentChatSyncOverlayObject,
  PursuitDocumentChatSyncRequest,
  PursuitDocumentChatSyncResponse,
  PursuitDocumentChatSyncRunObject,
  PursuitDocumentChatSyncRunRef,
  PursuitDocumentMsgCreate,
  Source,
} from '@/types/chat-v2'
import { UUID } from '@/types/Id'

export interface PursuitDocumentChatSyncQueryKeyArgs {
  orgId: UUID
  sessionId: UUID | ''
}

export const pursuitDocumentChatSyncQueryKeys = {
  cache: (args: PursuitDocumentChatSyncQueryKeyArgs) =>
    [
      'chat-session',
      'pursuit-document-sync',
      args.orgId,
      args.sessionId,
    ] as const,
}

const pursuitDocumentChatSessionQueryKey = (
  args: PursuitDocumentChatSyncQueryKeyArgs,
) =>
  ['chat-session', args.orgId, 'pursuit_document_chat', args.sessionId] as const

export interface PursuitDocumentChatSyncResult {
  didSync: boolean
  reason: string
  session: PursuitDocumentChatSession | null
  runState: ChatRunState
  activeRunId: UUID | null
  compactionStatus: ChatCompactionStatus
}

export async function sendPursuitDocumentChatCreateMessage(
  args: {
    sessionId: UUID
    content: PursuitDocumentMsgCreate['content']
    messageParams: PursuitDocumentMessageParams
  },
  accessToken: string,
): Promise<void> {
  const request: PursuitDocumentMsgCreate = {
    event_type: 'msg:create',
    content: args.content,
    ...args.messageParams,
  }

  const url = `${API_URL}/api/v1/chat/pursuit/document/sessions/${args.sessionId}/protocol/v3/messages`
  const res: Response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(request),
  })

  if (res.status === 422) {
    const validation = z
      .object({
        detail: z.union([z.string(), z.array(z.object({ msg: z.string() }))]),
      })
      .safeParse(await res.json().catch(() => null))
    const detail = validation.success
      ? typeof validation.data.detail === 'string'
        ? validation.data.detail
        : validation.data.detail
            .map(({ msg }) => msg.replace(/^Value error, /, ''))
            .join(' ')
      : undefined
    throw new ChatSendValidationError(
      detail?.slice(0, 2000) ||
        'The document request could not be submitted. Review its settings and try again.',
    )
  }
  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
      cause: {
        status: res.status,
      },
    })
  }
}

export function createEmptyPursuitDocumentChatSyncCache(): PursuitDocumentChatSyncCache {
  return createEmptyV3SyncCache<
    PursuitDocumentChatSyncDigest,
    PursuitDocumentChatSyncManifest,
    PursuitDocumentChatSyncResponse['meta_object'] extends infer T
      ? Exclude<T, null | undefined>
      : never,
    PursuitDocumentChatSyncResponse['view_object'] extends infer T
      ? Exclude<T, null | undefined>
      : never,
    PursuitDocumentChatSyncRunRef,
    PursuitDocumentChatSyncRunObject,
    PursuitDocumentChatSyncOverlayObject,
    PursuitDocumentChatSyncOverlayObject['messages'][number],
    PursuitDocumentChatSyncResponse['subagent_snapshot_objects'][number]
  >()
}

export function fetchPursuitDocumentChatSessionSync(
  queryClient: QueryClient,
  args: PursuitDocumentChatSyncQueryKeyArgs,
  accessToken: string,
  source: string = 'manual',
): Promise<PursuitDocumentChatSession> {
  return serializeV3SyncFetch('pursuit_document_chat', args.sessionId, () =>
    fetchPursuitDocumentChatSessionSyncNow(
      queryClient,
      args,
      accessToken,
      source,
    ),
  )
}

async function fetchPursuitDocumentChatSessionSyncNow(
  queryClient: QueryClient,
  args: PursuitDocumentChatSyncQueryKeyArgs,
  accessToken: string,
  source: string,
): Promise<PursuitDocumentChatSession> {
  if (!args.sessionId) {
    throw new Error('Session ID is required')
  }

  const existingCache =
    queryClient.getQueryData<PursuitDocumentChatSyncCache>(
      pursuitDocumentChatSyncQueryKeys.cache(args),
    ) ?? createEmptyPursuitDocumentChatSyncCache()

  const req: PursuitDocumentChatSyncRequest = {
    protocol_version: 1,
    known_object_ids: collectKnownV3SyncObjectIds(existingCache),
  }

  const url = `${API_URL}/api/v1/chat/pursuit/document/sessions/${args.sessionId}/protocol/v3/sync`
  const res: Response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(req),
  })

  if (!res.ok) {
    throw new SweetspotError({
      name: 'FETCH_ERROR',
      message: res.statusText,
      cause: {
        status: res.status,
      },
    })
  }

  const syncResponse = (await res.json()) as PursuitDocumentChatSyncResponse
  if (syncResponse.digest.session_id !== args.sessionId) {
    throw new Error(
      `Pursuit document chat sync session mismatch: expected ${args.sessionId}, got ${syncResponse.digest.session_id}`,
    )
  }

  const nextCache = mergePursuitDocumentChatSyncCache(
    existingCache,
    syncResponse,
  )
  const session = buildPursuitDocumentChatSessionFromSyncCache(nextCache)

  queryClient.setQueryData(
    pursuitDocumentChatSyncQueryKeys.cache(args),
    nextCache,
  )
  queryClient.setQueryData(pursuitDocumentChatSessionQueryKey(args), session)
  return session
}

export async function syncPursuitDocumentChatSessionIfNeeded(
  queryClient: QueryClient,
  args: PursuitDocumentChatSyncQueryKeyArgs,
  accessToken: string,
  digest: PursuitDocumentChatSyncDigest,
  source: string,
): Promise<PursuitDocumentChatSyncResult> {
  const cache = queryClient.getQueryData<PursuitDocumentChatSyncCache>(
    pursuitDocumentChatSyncQueryKeys.cache(args),
  )
  const decision = decideWhetherToSyncV3Digest(cache, digest)

  if (!decision.shouldSync) {
    return {
      didSync: false,
      reason: decision.reason,
      session: null,
      runState: toChatRunStateFromV3StreamState(digest.stream_state),
      activeRunId: toActiveRunIdFromV3Digest(digest),
      compactionStatus: getCompactionStatusFromV3Overlay(
        cache?.overlay_objects ?? {},
        digest.overlay_object_id,
      ),
    }
  }

  const session = await fetchPursuitDocumentChatSessionSync(
    queryClient,
    args,
    accessToken,
    `${source}:${decision.reason}`,
  )
  const nextCache = queryClient.getQueryData<PursuitDocumentChatSyncCache>(
    pursuitDocumentChatSyncQueryKeys.cache(args),
  )
  const nextDigest = nextCache?.digest ?? digest

  return {
    didSync: true,
    reason: decision.reason,
    session,
    runState: toChatRunStateFromV3StreamState(nextDigest.stream_state),
    activeRunId: toActiveRunIdFromV3Digest(nextDigest),
    compactionStatus: getCompactionStatusFromV3Overlay(
      nextCache?.overlay_objects ?? {},
      nextDigest.overlay_object_id,
    ),
  }
}

function mergePursuitDocumentChatSyncCache(
  cache: PursuitDocumentChatSyncCache,
  response: PursuitDocumentChatSyncResponse,
): PursuitDocumentChatSyncCache {
  return mergeV3SyncCache(cache, response)
}

export function mergePursuitDocumentChatSources(
  ...sourceLists: ReadonlyArray<ReadonlyArray<Source> | null | undefined>
): Source[] {
  const merged = new Map<number, Source>()
  for (const sources of sourceLists) {
    if (!sources) {
      continue
    }
    for (const source of sources) {
      merged.set(source.id, source)
    }
  }
  return Array.from(merged.values())
}

export function buildPursuitDocumentChatSessionFromSyncCache(
  cache: PursuitDocumentChatSyncCache,
): PursuitDocumentChatSession {
  if (cache.manifest == null) {
    throw new Error('Pursuit document chat sync manifest is unavailable')
  }

  const metaObject = cache.meta_objects[cache.manifest.meta_object_id]
  const viewObject = cache.view_objects[cache.manifest.view_object_id]
  if (!metaObject || !viewObject) {
    throw new Error(
      'Pursuit document chat sync cache is missing meta or view objects',
    )
  }

  const runRefById = new Map<UUID, PursuitDocumentChatSyncRunRef>(
    cache.manifest.run_refs.map((runRef) => [runRef.run_id, runRef]),
  )
  const messagesByRunId = new Map<UUID, ChatMessage[]>()

  for (const runRef of cache.manifest.run_refs) {
    const runObject = cache.run_objects[runRef.object_id]
    if (!runObject) {
      throw new Error(
        `Missing pursuit document chat run object ${runRef.object_id}`,
      )
    }
    messagesByRunId.set(
      runRef.run_id,
      buildPersistedRunMessagesFromV3Sync(runRefById, runRef, runObject),
    )
  }

  let overlayObject: PursuitDocumentChatSyncOverlayObject | undefined
  if (cache.manifest.overlay_object_id) {
    overlayObject = cache.overlay_objects[cache.manifest.overlay_object_id]
    if (!overlayObject) {
      throw new Error(
        `Missing pursuit document chat overlay object ${cache.manifest.overlay_object_id}`,
      )
    }
    messagesByRunId.set(
      overlayObject.run_id,
      buildOverlayMessagesFromV3Sync(overlayObject),
    )
  }

  const currentRunId =
    overlayObject?.run_id ?? viewObject.persisted_current_run_id
  const sources = mergePursuitDocumentChatSources(
    viewObject.sources,
    overlayObject?.state.sources,
  )
  const subagentSnapshotsByToolCallId = mergePersistedAndLiveSubagentSnapshots(
    viewObject.subagent_snapshot_object_ids_by_tool_call_id,
    overlayObject?.subagent_snapshot_object_ids_by_tool_call_id,
    cache.subagent_snapshot_objects,
  )

  const sharedUserIds =
    metaObject.shared_user_ids ?? metaObject.shared_with_user_ids ?? []

  return {
    id: metaObject.session_id,
    created_at: metaObject.created_at,
    last_modified: metaObject.last_modified,
    initial_model_name: metaObject.initial_model_name,
    initial_agent_name: metaObject.initial_agent_name,
    initial_agent_version: metaObject.initial_agent_version,
    all_messages: buildAllMessagesFromV3Sync(
      cache.manifest,
      messagesByRunId,
      overlayObject,
    ),
    current_thread: buildCurrentThreadFromV3Sync(
      cache.manifest,
      messagesByRunId,
      overlayObject,
      currentRunId,
    ),
    current_run_id: currentRunId,
    subagent_snapshots_by_tool_call_id: subagentSnapshotsByToolCallId,
    current_model_name: resolveCurrentModelNameFromV3Sync(
      cache.manifest,
      cache.run_objects,
      currentRunId,
      overlayObject,
      viewObject.current_model_name,
    ),
    shared_user_ids: sharedUserIds,
    pursuit_id: metaObject.pursuit_id,
    document_id: metaObject.document_id,
    created_by_user_id: metaObject.created_by_user_id,
    organization_id: metaObject.organization_id,
    title: viewObject.title,
    focus_organization_file_ids: viewObject.focus_organization_file_ids,
    focus_pursuit_file_ids: viewObject.focus_pursuit_file_ids,
    focus_user_file_ids: viewObject.focus_user_file_ids,
    sources,
    current_followups: viewObject.current_followups,
    stream_state: cache.digest?.stream_state ?? 'idle',
    pending_interaction: overlayObject?.pending_interaction ?? null,
  }
}
